import { useEffect } from 'react';
import { chatSend } from '../../../assets/images/images';
import { formatMessage, parseBulletPoints } from '../../../utility/helpers';
import { useSelectionState } from '../../../hooks/useSelectionState';
import OptionButton from './shared/OptionButton';
import MessageContainer from './shared/MessageContainer';
import { IncomingMessageAIProps } from './shared/types';

/**
 * IncomingMessageAI - For ChatWithAI (General chat/Job creation)
 * Features:
 * - Show all options without prioritization
 * - Preserve API order
 * - No "More/Less" toggle
 * - Simple selection logic
 */
const IncomingMessageAI = ({
    chat,
    message,
    isLoading,
    isFromJob = false,
    setLastMessageId,
    handleSendMessage,
    setEnableGenerateResponse,
    setLastQuestionStep,
    isLastMessage,
}: IncomingMessageAIProps) => {
    const isMultiSelect = chat?.is_multi_select;
    const {
        selectedOptions,
        optionKeys,
        renderKey,
        handleOptionClick,
        setSelectionsFromJSON
    } = useSelectionState(isMultiSelect);

    // Sync selections from chat.select_json
    useEffect(() => {
        setSelectionsFromJSON(chat?.select_json);
    }, [chat?.select_json, chat?.id, setSelectionsFromJSON]);

    // Set last message ID and enable response
    useEffect(() => {
        if (chat.id && !chat?.is_local && typeof chat.id === 'number') {
            setLastMessageId(chat.id);
        }
        if (chat?.step && typeof setLastQuestionStep === 'function') {
            setLastQuestionStep(chat?.step);
        }
        if (chat?.is_finished) {
            setEnableGenerateResponse(chat?.is_finished);
        }
    }, [chat, setLastQuestionStep, setLastMessageId, setEnableGenerateResponse]);

    const handleMultiClick = () => {
        if (selectedOptions.length === 0) return;

        if (!isMultiSelect && selectedOptions.length === 1) {
            const option = selectedOptions[0];
            const key = optionKeys.get(option);

            if (key) {
                handleSendMessage({ [key]: option }, option);
            } else {
                handleSendMessage([option], option);
            }
        } else {
            handleSendMessage(selectedOptions, '');
        }
    };

    const renderOptions = () => {
        // Disable options ONLY if it's not the last message.
        const isDisabled = !isLastMessage;

        if (message?.title && !Array.isArray(message?.title) && typeof message?.title === 'object' && message.title !== null) {
            // Object-based titles - show all, preserve order
            const entries = Object.entries(message.title)
                .filter(([key, value]) => key !== 'could_have')
                .filter(([key, value]) => !(value as string).toLowerCase().includes('what makes you a great candidate'));

            return (
                <>
                    {entries.map(([key, value], index: number) => {
                        const isSelected = selectedOptions.includes(value as string);
                        return (
                            <OptionButton
                                key={`${key}-${index}`}
                                value={value as string}
                                keyProp={key}
                                index={index}
                                isSelected={isSelected}
                                renderKey={renderKey}
                                isDisabled={isDisabled}
                                onClick={handleOptionClick}
                            />
                        );
                    })}
                </>
            );
        } else if (message?.title && Array.isArray(message?.title)) {
            // Array-based titles - show all, preserve API order
            const options = message.title
                .filter((option: string) => option !== 'Could-Haves')
                .filter((option: string) => !option.toLowerCase().includes('what makes you a great candidate'));

            return (
                <>
                    {options.map((option: string, index: number) => {
                        const isSelected = selectedOptions.includes(option);
                        return (
                            <OptionButton
                                key={`${option}-${index}`}
                                value={option}
                                keyProp={null}
                                index={index}
                                isSelected={isSelected}
                                renderKey={renderKey}
                                isDisabled={isDisabled}
                                onClick={handleOptionClick}
                            />
                        );
                    })}
                </>
            );
        }
        return null;
    };

    const generateDetailString = (details: Record<string, string | string[]>) => {
        return Object.entries(details).map(([key, value]) => {
            const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
            const formattedValue = Array.isArray(value) ? value.join(', ') : value;

            return (
                <div key={key} className="final-received">
                    <span className="inline-medium-text">{formattedKey}:</span> {formattedValue}
                </div>
            );
        });
    };

    const renderMessageContent = () => {
        if (!message) {
            return <p><span className="inline-bold-text">Hunter: </span></p>;
        }

        if (message?.is_initial) {
            const messageText = message?.message || "Welcome! Let's build your job description. What position are you creating this for? Feel free to share the job title and any key details you have in mind.";
            return <p><span className="inline-bold-text">Hunter: </span>{messageText}</p>;
        }

        if (message?.out_of_context === true) {
            const outOfContextMessage = Array.isArray(message?.message)
                ? message.message[0]
                : message?.message || "Sorry, that's out of my context. I can only help with job descriptions and candidate requirements.";

            return (
                <p>
                    <span className="inline-bold-text">Hunter: </span>
                    {outOfContextMessage}
                </p>
            );
        }

        if (message?.general_info === true) {
            const generalInfoMessage = Array.isArray(message?.message)
                ? message.message[0]
                : message?.message || "Here's the information you requested.";

            return (
                <p>
                    <span className="inline-bold-text">Hunter: </span>
                    {generalInfoMessage}
                </p>
            );
        }

        if (message?.title && (Array.isArray(message?.title) || (typeof message?.title === 'object' && message.title !== null))) {
            const messageText = message?.message ? formatMessage(message.message) : "Please choose from the following options:";
            return (
                <p>
                    <span className="inline-bold-text">Hunter: </span>
                    {messageText}
                </p>
            );
        }

        if (message?.step === 'finish') {
            return (
                <p>
                    <span className="inline-bold-text">Hunter:</span>
                    Finished! These are details based on your provided job info:-
                    <br /><br />
                    {message?.summary && generateDetailString(message?.summary)}
                    <br />
                    Let me know if you want to change or update anything?
                </p>
            );
        }

        const messageText =
            message?.step === 'distance'
                ? `${formatMessage(message?.message)} Distances are calculated using your office location to ensure precision.`
                : formatMessage(message?.message);

        const bulletData = messageText ? parseBulletPoints(messageText) : null;

        if (bulletData?.bulletItems.length) {
            return (
                <p>
                    <span className="inline-bold-text">Hunter: </span>
                    {bulletData.introText && (
                        <>
                            <span>{bulletData.introText}</span>
                            <br />
                        </>
                    )}
                    <ul className="message-list">
                        {bulletData.bulletItems.map((item: string, index: number) => (
                            <li key={index}>{item}</li>
                        ))}
                    </ul>
                    {bulletData.closingText && (
                        <span>{bulletData.closingText}</span>
                    )}
                </p>
            );
        }

        return <p><span className="inline-bold-text">Hunter: </span>{messageText}</p>;
    };

    return (
        <>
            <MessageContainer isFromJob={isFromJob}>
                {renderMessageContent()}
            </MessageContainer>

            {((Array.isArray(message?.title) && message.title.length > 0) ||
                (!Array.isArray(message?.title) && message?.title && (typeof message?.title === 'object' && message.title !== null))) && (
                    <div className={`chat-single-option no-image-option ${isLoading ? 'p-none' : ''}`}>
                        {renderOptions()}
                        {(!chat?.select_json || isLastMessage) && (
                            <img
                                src={chatSend}
                                alt="send"
                                className={`chat-send-btn ${selectedOptions?.length > 0 ? '' : 'p-none'}`}
                                onClick={handleMultiClick}
                                role="button"
                                aria-label="Send selected options"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleMultiClick();
                                    }
                                }}
                            />
                        )}
                    </div>
                )}
        </>
    );
};

export default IncomingMessageAI;

